<script lang="ts" setup>
import type { ButtonColor } from '@/components/button/SkrButton.vue'
import SkrButton from '@/components/button/SkrButton.vue'

interface Props {
  title: string
  maxWidth?: string
  actionText?: string
  actionColor?: ButtonColor
  cancelText?: string
  disabled?: boolean
  loading?: boolean
  persistent?: boolean
  dataCy?: string
  // dark header
  surfaceVariant?: boolean
  cardClass?: string
  cardStyle?: Record<string, string>
}

withDefaults(defineProps<Props>(), {
  maxWidth: '570',
  actionColor: 'primary-xqes',
  surfaceVariant: false,
})

const emit = defineEmits<{
  (event: 'action' | 'close'): void
}>()

const { t } = useI18n()

const modelValue = ref(true)

watchEffect(() => {
  if (!modelValue.value) {
    emit('close')
  }
})
</script>

<template>
  <!-- scroll-strategy on v-dialog set to 'none' because of a bug in v-dialog: the v-overlay inside v-dialog is not mounted on time 
   before the dialog tries to access scrolling behavior on the overlay  -->
  <v-dialog
    :model-value="modelValue"
    :max-width="maxWidth"
    :persistent="persistent"
    :close-on-back="true"
    scroll-strategy="none"
  >
    <v-card :data-cy="dataCy" :class="cardClass" :style="cardStyle">
      <v-card-title class="whitespace-normal" :class="{ 'base-dialog__title--variant': surfaceVariant }">
        {{ title }}
      </v-card-title>
      <v-card-text>
        <slot />
      </v-card-text>
      <slot name="actions">
        <v-divider />
        <v-card-actions>
          <skr-button
            data-cy="dialog_cancel_button"
            type="secondary"
            size="xl"
            color="primary-xqes"
            @click="emit('close')"
          >
            {{ cancelText ?? t('global.no_thanks') }}
          </skr-button>
          <v-spacer />
          <skr-button
            data-cy="dialog_action_button"
            size="xl"
            :color="actionColor"
            :disabled="disabled"
            :loading="loading"
            @click="emit('action')"
          >
            {{ actionText ?? t('global.accept') }}
          </skr-button>
        </v-card-actions>
      </slot>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.base-dialog__title--variant {
  background: rgb(var(--v-theme-surface-variant-xqes));
  color: rgb(var(--v-theme-on-surface-variant-xqes));
}
</style>
